<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'

interface Props {
  currency?: string
  balance?: string
  lastUpdated?: string
  hasError?: boolean
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  currency: 'AED',
  balance: '',
  lastUpdated: '',
  hasError: false,
  loading: false,
})

defineEmits<{
  (e: 'retry'): void
}>()
</script>

<template>
  <Box align="center" class="ab-card">
    <Box class="ab-card__blur ab-card__blur--tr" />
    <Box class="ab-card__blur ab-card__blur--bl" />

    <Box direction="col" gap="6" class="ab-card__inner">
      <Box align="start" justify="between" class="ab-card__top">
        <Text type="body" weight="medium" color="muted" is="span" class="ab-card__label">
          Available Balance
        </Text>
        <PiIcon name="wallet" class="ab-card__icon" />
      </Box>

      <Box v-if="hasError" direction="col" gap="2">
        <Text type="heading" color="danger" weight="medium" is="span">
          Couldn't load balance
        </Text>
        <Box align="center" gap="3">
          <Text type="caption" color="muted" is="span">
            Check your connection and try again.
          </Text>
          <AppButton
            label="Retry"
            variant="text"
            color="primary"
            size="small"
            @click="$emit('retry')"
          />
        </Box>
      </Box>

      <Box v-else direction="col" gap="3">
        <Box align="baseline" gap="2" class="ab-card__amount-row">
          <Text type="heading" color="muted" weight="medium" is="span" class="ab-card__currency">
            {{ currency }}
          </Text>
          <Text type="display-lg" color="default" is="span" class="ab-card__amount">
            {{ loading ? '—' : balance || '0.00' }}
          </Text>
        </Box>
        <Box v-if="lastUpdated" align="center" gap="2">
          <Box class="ab-card__dot" />
          <Text
            type="body"
            weight="medium"
            color="muted"
            is="span"
            class="ab-card__updated-text"
          >
            Last updated &middot; {{ lastUpdated }}
          </Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.ab-card {
  position: relative;
  flex: 1;
  min-width: 0;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
  height: 160px;
  box-shadow: 0 4px 12px rgba(10, 13, 18, 0.06);
  overflow: hidden;
  box-sizing: border-box;
}

.ab-card__blur {
  position: absolute;
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  pointer-events: none;
}

.ab-card__blur--tr {
  background: var(--p-primary-50);
  filter: blur(12px);
  bottom: 55px;
  right: 10px;
}

.ab-card__blur--bl {
  background: var(--p-primary-50);
  filter: blur(16px);
  bottom: -28px;
  left: -22px;
  opacity: 0.7;
}

.ab-card__inner {
  width: 100%;
  position: relative;
  z-index: 1;
}

.ab-card__top {
  height: 20px;
}

.ab-card__label {
  line-height: 20px;
  white-space: nowrap;
}

.ab-card__icon {
  font-size: 40px;
  color: var(--p-primary-600);
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: -4px;
}

.ab-card__amount-row {
  line-height: 1;
}

.ab-card__currency {
  line-height: 30px;
}

.ab-card__amount {
  line-height: 38px;
}

.ab-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 10px;
  background: var(--p-success-700);
  flex-shrink: 0;
}

.ab-card__updated-text {
  line-height: 20px;
  white-space: nowrap;
}
</style>
